import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Receipt, Plus, Search, Filter, Plane, CreditCard, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { hasPermission } from '../../constants/permissions';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TravelBookingDesk = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [requests, setRequests] = useState([]);
  const [creditCards, setCreditCards] = useState([]);
  const [showBookingModal, setShowBookingModal] = useState(false);

  const [bookingForm, setBookingForm] = useState({
    request_id: '',
    booking_type: 'flight',
    pnr_reference: '',
    vendor_name: 'IndiGo Airlines',
    carrier_name_or_no: '6E-204',
    base_fare: 10000,
    taxes: 1800,
    convenience_fee: 200,
    credit_card_used_id: '',
    cancellation_policy: 'Non-refundable within 24 hrs',
  });

  useEffect(() => {
    fetchBookings();
    fetchRequests();
    fetchCreditCards();
  }, []);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/travel-desk/bookings`, { withCredentials: true });
      setBookings(data.bookings || []);
    } catch (err) {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const fetchRequests = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/travel-desk/requests`, { withCredentials: true });
      const approved = (data.requests || []).filter((r) => ['approved', 'booked'].includes(r.status));
      setRequests(approved);
      if (approved.length > 0) {
        setBookingForm((prev) => ({ ...prev, request_id: approved[0]._id }));
      }
    } catch (err) {}
  };

  const fetchCreditCards = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/travel-desk/credit-cards`, { withCredentials: true });
      setCreditCards(data.credit_cards || []);
    } catch (err) {}
  };

  const handleSaveBooking = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/travel-desk/bookings`, bookingForm, { withCredentials: true });
      toast.success('Ticket booking recorded!');
      setShowBookingModal(false);
      fetchBookings();
    } catch (err) {
      toast.error('Failed to save booking');
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto font-sans">
      <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-blue-950 text-white rounded-2xl p-6 shadow-xl flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-amber-500/20 text-amber-400 rounded-xl border border-amber-500/30">
            <Receipt className="w-7 h-7" />
          </div>
          <div>
            <h1 className="text-2xl font-black">Booking Desk Operations</h1>
            <p className="text-xs text-blue-200">
              Record PNR, ticket generation, carrier details, and corporate card billing.
            </p>
          </div>
        </div>

        {hasPermission(user, 'travel_desk.booking.edit') && (
          <button
            onClick={() => setShowBookingModal(true)}
            className="px-5 py-2.5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs rounded-xl shadow-md flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Book Approved Request
          </button>
        )}
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-4 bg-slate-50 border-b border-slate-200 font-bold text-xs text-slate-800">
          Confirmed Ticket &amp; Booking Register ({bookings.length})
        </div>

        {loading ? (
          <div className="p-8 text-center text-slate-500 font-medium">Loading bookings...</div>
        ) : bookings.length === 0 ? (
          <div className="p-12 text-center text-slate-400 font-semibold italic">No bookings recorded yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead className="bg-slate-100 text-slate-700 font-bold uppercase text-[10px]">
                <tr>
                  <th className="p-3.5">Booking Ref</th>
                  <th className="p-3.5">PNR</th>
                  <th className="p-3.5">Employee</th>
                  <th className="p-3.5">Carrier / Flight</th>
                  <th className="p-3.5">Vendor</th>
                  <th className="p-3.5">Credit Card</th>
                  <th className="p-3.5 text-right">Total Amount</th>
                  <th className="p-3.5">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 font-medium text-slate-800">
                {bookings.map((b) => (
                  <tr key={b._id} className="hover:bg-slate-50">
                    <td className="p-3.5 font-mono font-bold text-blue-600">{b.booking_number}</td>
                    <td className="p-3.5 font-mono font-bold text-slate-900">{b.pnr_reference}</td>
                    <td className="p-3.5 font-bold">{b.employee_name}</td>
                    <td className="p-3.5">{b.carrier_name_or_no}</td>
                    <td className="p-3.5">{b.vendor_name}</td>
                    <td className="p-3.5 font-mono text-slate-600">{b.credit_card_masked}</td>
                    <td className="p-3.5 font-mono font-bold text-emerald-700 text-right">
                      ₹ {(b.total_amount || 0).toLocaleString('en-IN')}
                    </td>
                    <td className="p-3.5">
                      <span className="px-2.5 py-1 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800 uppercase">
                        {b.booking_status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Booking Modal */}
      {showBookingModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 space-y-4">
            <div className="flex justify-between items-center border-b border-slate-200 pb-3">
              <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                <Receipt className="w-5 h-5 text-amber-500" /> Book Approved Travel Request
              </h3>
              <button onClick={() => setShowBookingModal(false)} className="text-slate-400 hover:text-slate-600 font-bold">
                &times;
              </button>
            </div>

            <form onSubmit={handleSaveBooking} className="space-y-3 text-xs">
              <div>
                <label className="block font-bold text-slate-700 mb-1">Select Travel Request</label>
                <select
                  value={bookingForm.request_id}
                  onChange={(e) => setBookingForm({ ...bookingForm, request_id: e.target.value })}
                  className="w-full h-9 px-2.5 bg-slate-50 border border-slate-300 rounded-xl font-bold"
                >
                  {requests.map((r) => (
                    <option key={r._id} value={r._id}>
                      {r.request_number} — {r.employee_name} ({r.purpose})
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">PNR / Reference Number</label>
                  <input
                    type="text"
                    value={bookingForm.pnr_reference}
                    onChange={(e) => setBookingForm({ ...bookingForm, pnr_reference: e.target.value })}
                    className="w-full h-9 px-2.5 bg-slate-50 border border-slate-300 rounded-xl font-mono uppercase font-bold"
                    placeholder="PNR12345"
                    required
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Carrier / Flight / Train No</label>
                  <input
                    type="text"
                    value={bookingForm.carrier_name_or_no}
                    onChange={(e) => setBookingForm({ ...bookingForm, carrier_name_or_no: e.target.value })}
                    className="w-full h-9 px-2.5 bg-slate-50 border border-slate-300 rounded-xl font-medium"
                    placeholder="6E-204"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Base Fare (₹)</label>
                  <input
                    type="number"
                    value={bookingForm.base_fare}
                    onChange={(e) => setBookingForm({ ...bookingForm, base_fare: Number(e.target.value) })}
                    className="w-full h-9 px-2 bg-slate-50 border border-slate-300 rounded-xl font-mono font-bold"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Taxes (₹)</label>
                  <input
                    type="number"
                    value={bookingForm.taxes}
                    onChange={(e) => setBookingForm({ ...bookingForm, taxes: Number(e.target.value) })}
                    className="w-full h-9 px-2 bg-slate-50 border border-slate-300 rounded-xl font-mono font-bold"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Fees (₹)</label>
                  <input
                    type="number"
                    value={bookingForm.convenience_fee}
                    onChange={(e) => setBookingForm({ ...bookingForm, convenience_fee: Number(e.target.value) })}
                    className="w-full h-9 px-2 bg-slate-50 border border-slate-300 rounded-xl font-mono font-bold"
                  />
                </div>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Select Corporate Credit Card</label>
                <select
                  value={bookingForm.credit_card_used_id}
                  onChange={(e) => setBookingForm({ ...bookingForm, credit_card_used_id: e.target.value })}
                  className="w-full h-9 px-2.5 bg-slate-50 border border-slate-300 rounded-xl font-medium"
                >
                  <option value="">-- Direct Corporate Invoice --</option>
                  {creditCards.filter(c => c.status === 'active').map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.card_name} ({c.card_number_masked}) — Avail: ₹ {(c.available_limit || 0).toLocaleString('en-IN')}
                    </option>
                  ))}
                </select>
              </div>

              <div className="pt-3 border-t border-slate-200 flex justify-end gap-2">
                <button type="button" onClick={() => setShowBookingModal(false)} className="px-4 py-2 bg-slate-200 font-bold rounded-xl">
                  Cancel
                </button>
                <button type="submit" className="px-5 py-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold rounded-xl shadow-md">
                  Confirm Booking
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TravelBookingDesk;
